<template>
  <template v-for="tag in displayData" :key="tag.id">
    <ScrollEventList
      v-if="tag.events.length > MAX_DISPLAY_EVENTS"
      :event-list-title="tag.name"
      :events="tag.events.slice(0, MAX_DISPLAY_EVENTS)"
      :link-to-full-list="tag.url"
      :event-link-append-query="`referrer_new_feature_${tag.id}`"
    />
    <ScrollEventList
      v-else-if="tag.events.length > 0"
      :event-list-title="tag.name"
      :events="tag.events"
      :event-link-append-query="`referrer_new_feature_${tag.id}`"
    />
  </template>
</template>

<script setup lang="ts">
import { useMainStore } from '@/store'
import SearchParams from '@/utils/search/SearchParams'
import { buildQueryString } from '@/utils/url-action'

const ScrollEventList = defineAsyncComponent(() => import('@/components/event/ScrollEventList.vue'))

const MAX_DISPLAY_EVENTS = 6
const store = useMainStore()

interface RecommendedFeatureTag {
  readonly name: string
  readonly url: string
}

interface ApiRecommendedFeatureTag extends RecommendedFeatureTag {
  readonly id: number
}

type Event = unknown

interface DisplayData {
  readonly id: number
  readonly name: string
  readonly url: string
  readonly events: Event[]
}

function formatUrl(url: string): string {
  const urlObject = new URL(url)
  return `${urlObject.pathname}${urlObject.search}`
}

function clientToApiUrl(url: string): string {
  const router = useRouter()
  const route = router.resolve(url)

  const paramsObject = new SearchParams(store, route)
  const searchConditions = paramsObject.apiSearchParams

  /* API結果の制限を追加する。
  　そして、「すべて見る」を表示かどうか決められるため、表示イベントの最大数以上の件数でデータ習得するとなっています。 */
  searchConditions['per_page'] = (MAX_DISPLAY_EVENTS + 1).toString()
  searchConditions['filter[randomize]'] = true
  const query = buildQueryString(searchConditions)

  return `events/${query}`
}

const featuredTags = new Map<number, RecommendedFeatureTag>()
try {
  const { data } = await store.dispatch('apiGet', 'top_recommended_feature_tags')
  if (data.top_recommended_feature_tags) {
    const topRecommendedFeatureTags: ApiRecommendedFeatureTag[] = data.top_recommended_feature_tags
    for (const tag of topRecommendedFeatureTags) {
      const formattedUrl = formatUrl(`${tag.url}${tag.url.includes('?') ? '&' : '?'}referrer_new_feature_${tag.id}`)
      featuredTags.set(tag.id, { name: tag.name, url: formattedUrl })
    }
  }
} catch (err) {
  console.error(err)
}

const apiUrls = new Map<number, string>()
for (const [id, tag] of featuredTags) {
  const url = clientToApiUrl(tag.url)
  if (url) {
    apiUrls.set(id, url)
  }
}

const apiCalls = Array.from(apiUrls, ([id, url]) => async (): Promise<[number, Event[]]> => {
  const { data } = await store.dispatch('apiGetV2', url)
  if (data?.events && Array.isArray(data.events)) {
    return [id, data.events]
  }
  throw Error('Returned data is not an event search result')
}).map(fn => fn())

const apiCallResults = await Promise.allSettled(apiCalls)
const events = new Map<number, Event[]>(apiCallResults.flatMap(result => (result.status === 'fulfilled' ? [result.value] : [])))

const displayData: DisplayData[] = Array.from(events, ([id, featuredEvents]) => ({
  id,
  name: featuredTags.get(id)?.name || '',
  url: featuredTags.get(id)?.url || '',
  events: featuredEvents
}))
</script>
